// TODO: update file to use api client and proper types
import { Injectable } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import {
    ArpClient,
    ArpExceptionDetailDataModelDto,
    CheckExceptionClient,
    ExceptionCorrectionModelDto,
    InstitutionCheckExceptionReturnReasonClient,
    SaveCheckExceptionAttachmentRequestModelDto,
    SearchCheckExceptionModelDto,
} from '@treasury/api/channel';
import { CheckException as ArpCheckException } from '../../../arp';
import { downloadBlob, toBase64 } from '../../../utilities/file-handling.js';
import CheckExceptionMappings from '../../mappings/positive-pay/check-exceptions/check-exception-mappings.js';
import { PositivePayExceptionRequests } from '../../requests/positive-pay/positive-pay-exception-requests.js';
import { CheckException } from './entities/check-exception';
import { CheckExceptionAggregate } from './entities/check-exception-aggregate';
import { CheckExceptionReturnReason } from './entities/check-exception-return-reason';
import { CheckExceptionHoldover, CheckExceptionSearchParameters } from './positive-pay.types';

@Injectable()
export class CheckExceptionsService {
    constructor(
        private readonly checkExceptionClient: CheckExceptionClient,
        private readonly arpClient: ArpClient,
        private readonly institutionCheckExceptionReturnReasonClient: InstitutionCheckExceptionReturnReasonClient
    ) {}

    /**
     * @deprecated
     * use submitCheckExceptionDecisions instead
     */
    public static async saveCheckExceptions(records: CheckExceptionHoldover[]) {
        // TODO: This should be cleaned up to only post the required fields.
        const exceptions = records.map(record => {
            const exception = {
                ...record,
                pay: record.decisionChoice === 'Pay',
                existingDecision: record.decisionChoice === record.decisionTaken,
                returnReasonUniqueId: record.returnReason,
                returnReasonComment: record.comment,
            };
            // @ts-expect-error: deleting this prop is okay for API payload
            delete exception.returnReason;
            // @ts-expect-error: deleting this prop is okay for API payload
            delete exception.initialDecision;
            // @ts-expect-error: deleting this prop is okay for API payload
            delete exception.decisionChoice;
            // @ts-expect-error: deleting this prop is okay for API payload
            delete exception.pendingDecision;
            return exception;
        });
        return PositivePayExceptionRequests.saveCheckExceptions(exceptions);
    }

    public static async submitCheckExceptionDecisions(
        checkExceptionViewModels: ArpCheckException[]
    ) {
        // submitCheckExceptionDecisions cleaner version of legacy code above
        return PositivePayExceptionRequests.saveCheckExceptions(
            checkExceptionViewModels.map(exception => exception.getDtoForSubmit())
        );
    }

    public static async saveAttachmentAndComment(
        checkException: ArpCheckException,
        newComment: string,
        fileToUpload: File | undefined
    ) {
        const body: {
            Attachment?: File;
            FileName?: string;
            Comment?: string;
        } = {};
        if (fileToUpload) {
            body.Attachment = await toBase64(fileToUpload);
            body.FileName = fileToUpload.name;
        }
        if (typeof newComment !== 'undefined') {
            body.Comment = newComment;
        }

        if (Object.keys(body).length !== 0) {
            return PositivePayExceptionRequests.saveAttachmentAndComment(body, checkException.guid);
        }
        return false;
    }

    /**
     * @deprecated This static method is deprecated. Please use the instance method instead.
     */
    public static async searchCheckExceptions(searchData: {
        parameters: CheckExceptionSearchParameters;
    }) {
        let body = {};
        const { parameters } = searchData;
        if (Object.keys(parameters).length) {
            body = CheckExceptionMappings.mapSearchParams(parameters);
            body = {
                ...body,
                type: 'OpenItems',
            };
        }
        const searchCheckExceptionsResponse =
            await PositivePayExceptionRequests.searchCheckExceptions(body);

        if (!searchCheckExceptionsResponse.items) {
            return { data: [], totalCount: 0 };
        }

        const today = new Date();
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);

        const mapped = searchCheckExceptionsResponse.items.map(exception => {
            const lastDecisionDateTime = new Date(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (exception as Record<string, any>).lastDecisionDate as string
            );
            const lastDecisionDate = new Date(
                lastDecisionDateTime.getFullYear(),
                lastDecisionDateTime.getMonth(),
                lastDecisionDateTime.getDate()
            );

            return {
                ...exception,
                decisionChoice: exception.decisionStatus,
                initialDecision: exception.decisionStatus,
                pendingDecision:
                    lastDecisionDate.getTime() !== today.getTime() && exception.protected !== 'Y'
                        ? 'true'
                        : 'false',
                returnReason: exception.returnReasonUniqueId,
                // Notes of the business logic we know so far:
                // If the last decision is the FI, it hasn't been worked
                // If the audit action is Pay or Return and from a real user, it's been worked
                // If the return reason is set, it's been worked
                // all have to occur during the same day
                userDecisionedToday: exception.audits?.reduce((prev, audit) => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const sinceYesterday = new Date(audit.timestamp) >= yesterday;
                    const decisionNotFromFI =
                        exception.lastDecisionTakenBy !== 'Financial Institution';
                    const isDecision =
                        audit.action === 'Return' ||
                        audit.action === 'Pay' ||
                        (exists(exception.returnReasonId) && exception.decisionStatus === 'Return');

                    return prev || (decisionNotFromFI && sinceYesterday && isDecision);
                }, false),
            };
        });

        return {
            data: mapped,
            totalCount: searchCheckExceptionsResponse?.items.length,
            toDecideCount: mapped.filter(
                item => item.protected !== 'Y' && !item.userDecisionedToday
            ).length,
        };
    }

    public static async decisionActivity(searchData: {
        parameters: CheckExceptionSearchParameters;
    }) {
        let body = {};
        const { parameters } = searchData;
        if (Object.keys(parameters).length) {
            body = CheckExceptionMappings.mapSearchParams(parameters);
            body = {
                ...body,
                decisionStatus: parameters.decisionTaken,
                type: 'History',
            };
        }

        const searchCheckExceptionsResponse =
            await PositivePayExceptionRequests.searchCheckExceptions(body);

        if (!searchCheckExceptionsResponse.items) {
            return { data: [], totalCount: 0 };
        }

        return {
            data: searchCheckExceptionsResponse.items,
            totalCount: searchCheckExceptionsResponse?.items.length,
        };
    }

    public static async fetchCheckImages(checkImageNumber: string) {
        return PositivePayExceptionRequests.fetchCheckImages(checkImageNumber);
    }

    public static async saveCorrectionRequest(
        file: File,
        correctionRequest: ExceptionCorrectionModelDto
    ) {
        const body = { ...correctionRequest };
        if (file) {
            const encodedFile = await toBase64(file);
            body.attachment = encodedFile;
            body.attachmentFileName = file.name || '';
        }
        return PositivePayExceptionRequests.saveCorrectionRequest(body);
    }

    /**
     * @deprecated This static method is deprecated. Please use the instance method instead.
     */
    public static async getCheckExceptionAttachment(arpExceptionDetailUniqueId: string) {
        const response = await PositivePayExceptionRequests.getCheckExceptionAttachment(
            arpExceptionDetailUniqueId
        );
        const filename = response.headers.get('x-filename');
        const contentType = response.headers.get('content-type');
        const buffer = await response.arrayBuffer();
        const blob = new Blob([buffer], { type: contentType ?? '' });

        return downloadBlob(blob, filename);
    }

    /**
     * @deprecated This static method is deprecated. Please use the instance method instead.
     */
    public static async getCheckExceptionReturnReasons() {
        return PositivePayExceptionRequests.getReturnReasons();
    }

    /**
     * Saves decisions for a list of check exceptions.
     *
     * This method takes an array of {@link CheckExceptionHoldover} objects, transforms each one
     * into an {@link ArpExceptionDetailDataModelDto} suitable for the API, and then submits
     * these transformed exceptions to the backend via {@link ArpClient.arpSaveArpExceptions}.
     *
     * @param records An array of {@link CheckExceptionHoldover} objects, each representing a check
     *                exception with a pending decision.
     * @returns A promise that resolves with the response from the {@link ArpClient.arpSaveArpExceptions}
     *          API call. The exact structure of the response depends on the API.
     * @throws {Error} Throws an error if the API call via {@link ArpClient.arpSaveArpExceptions} fails.
     */
    public async saveCheckExceptions(records: CheckException[]) {
        const exceptions = records.map(record => record.toSaveDto());

        // HACK: because the open api docs are wrong and expect an incomplete object type
        return this.arpClient.arpSaveArpExceptions(
            exceptions as unknown as ArpExceptionDetailDataModelDto[]
        );
    }

    /**
     * Retrieves an attachment associated with a specific check exception and initiates its download.
     *
     * This method fetches the attachment file for a check exception identified by its unique ID.
     * It calls `this.checkExceptionClient.checkExceptionGetAttachment` to get the raw attachment data
     * and metadata. The filename is extracted from the 'x-filename' response header, and the
     * content type from the 'content-type' header. The attachment data is then converted into a `Blob`.
     * Finally, the `downloadBlob` utility function is used to trigger a browser download of the file.
     *
     * @param arpExceptionDetailUniqueId The unique identifier of the check exception for which
     *                                   the attachment should be retrieved and downloaded.
     * @returns A promise that resolves when the download process is initiated by `downloadBlob`.
     *          The exact return value depends on the `downloadBlob` utility function (often `void`
     *          or `Promise<void>`). The primary outcome is the file download.
     * @throws {Error} Throws an error if the API call via `this.checkExceptionClient.checkExceptionGetAttachment`
     *                 fails or if there's an issue processing the response (e.g., missing headers,
     *                 problems converting data to a Blob).
     */
    public async getCheckExceptionAttachment(arpExceptionDetailUniqueId: string) {
        const response = await PositivePayExceptionRequests.getCheckExceptionAttachment(
            arpExceptionDetailUniqueId
        );

        // TODO: uncomment this when the API is updated
        // const response = await this.checkExceptionClient.checkExceptionGetAttachment(
        //     arpExceptionDetailUniqueId
        // );

        const responseData = response;
        const filename = responseData.headers.get('x-filename');
        const contentType = responseData.headers.get('content-type');
        const buffer = await responseData.arrayBuffer();
        const blob = new Blob([buffer], { type: contentType ?? '' });

        return downloadBlob(blob, filename);
    }

    /**
     * Saves an attachment and/or a comment for a specific check exception.
     *
     * This method prepares a request to save an attachment file and/or a textual comment
     * associated with a given {@link ArpCheckException}. If a file is provided, it's
     * converted to a base64 string for the request. The operation is performed
     * by calling the `checkExceptionSaveAttachment` method on the `checkExceptionClient`.
     *
     * @param checkException The check exception entity (instance of {@link ArpCheckException})
     *                       for which the attachment and/or comment is being saved. Its `guid`
     *                       is used to identify the exception in the backend.
     * @param newComment The textual comment to be saved. This is a required string,
     *                   though it can be an empty string.
     * @param fileToUpload An optional {@link File} object representing the attachment.
     *                     If provided, its content will be base64 encoded and its name
     *                     will be included in the request. If `undefined`, no file is attached.
     * @returns A promise that resolves with the data payload from the API response upon
     *          successful execution of the save operation. The specific structure of this
     *          data is determined by the `checkExceptionSaveAttachment` API endpoint.
     * @throws {Error} Throws an error if the API call through `this.checkExceptionClient.checkExceptionSaveAttachment` fails.
     *                 The method also includes an explicit check `if (Object.keys(body).length === 0)`
     *                 intending to throw an error with the message 'No attachment or comment provided';
     *                 however, due to the current implementation where `newComment` (as `body.comment`)
     *                 is always part of the request body, this specific error condition is unlikely to be met.
     */
    public async saveAttachmentAndComment(
        checkException: ArpCheckException,
        newComment: string,
        fileToUpload: File | undefined
    ) {
        const body: SaveCheckExceptionAttachmentRequestModelDto = {
            attachment: fileToUpload ? await toBase64(fileToUpload) : undefined,
            fileName: fileToUpload?.name,
            comment: newComment,
        };

        if (Object.keys(body).length === 0) {
            throw new Error('No attachment or comment provided');
        }
        const response = await this.checkExceptionClient.checkExceptionSaveAttachment(
            checkException.guid,
            body
        );

        return response.data;
    }

    /**
     * Searches for check exceptions based on the provided search parameters.
     *
     * This method constructs a search query using the provided {@link CheckExceptionSearchParameters}.
     * It then calls the backend service to retrieve matching check exceptions.
     * The raw results are transformed into an array of {@link CheckException} domain entities
     * and packaged within a {@link CheckExceptionAggregate} object, which also includes
     * counts for total items and items requiring a decision.
     *
     * @param searchData An object encapsulating the search parameters.
     *                   Its `parameters` property holds the actual search criteria.
     * @param searchData.parameters The {@link CheckExceptionSearchParameters} defining the criteria
     *                              for filtering check exceptions.
     * @returns A promise that resolves to a {@link CheckExceptionAggregate}. This aggregate
     *          contains the list of found {@link CheckException} instances, the total number
     *          of matching exceptions, and the count of exceptions that are not protected
     *          and have not been decisioned by the user today. If no items are found or
     *          the API response is malformed, an empty aggregate is returned.
     */
    public async searchCheckExceptions(searchData: { parameters: CheckExceptionSearchParameters }) {
        let body = {};
        const { parameters } = searchData;
        if (Object.keys(parameters).length) {
            body = CheckExceptionMappings.mapSearchParams(parameters);
            body = {
                ...body,
                type: 'OpenItems',
            };
        }

        const searchCheckExceptionsResponse =
            await this.checkExceptionClient.checkExceptionSearchCheckExceptions(
                body as SearchCheckExceptionModelDto
            );

        if (!searchCheckExceptionsResponse.data || !searchCheckExceptionsResponse.data.items) {
            return new CheckExceptionAggregate([], 0, 0);
        }

        const today = new Date();
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);

        const mapped: CheckException[] = searchCheckExceptionsResponse.data.items.map(
            exception => new CheckException(exception)
        );

        return new CheckExceptionAggregate(
            mapped,
            searchCheckExceptionsResponse?.data.items.length,
            mapped.filter(item => !item.protected && !item.userDecisionedToday).length
        );
    }

    /**
     * Fetches check exception return reasons from the institution.
     *
     * @returns {Promise<CheckExceptionReturnReason[]>} A promise that resolves to an array of CheckExceptionReturnReason objects.
     */
    public async getCheckExceptionReturnReasons() {
        const response =
            await this.institutionCheckExceptionReturnReasonClient.institutionCheckExceptionReturnReasonGetCheckExceptionReturnReasons();
        return response.data.map(dto => new CheckExceptionReturnReason(dto));
    }
}
